
import i18n from '@/i18n';
import Vue, { PropType } from 'vue';
import DOMPurify from 'dompurify';
import { UserModel } from '@descomplica/dex-models/lib/models/User';
import { Accordion, Icon, OutlinedTextInput, TimelineItem } from '@descomplica/vue-components';
import {
  AssessmentResult,
  ASSESSMENT_RESULT_STATUS,
  EvaluationSheet,
} from '@/models/greathall/assessment.model';
import { LocaleMessage } from 'vue-i18n';

interface GradeGroups {
  name: string;
  grades: {
    name: string;
    value: number;
  }[];
}

export default Vue.extend({
  components: { Accordion, Icon, OutlinedTextInput, TimelineItem },
  props: {
    assessmentResult: {
      type: Object as PropType<AssessmentResult>,
      required: true,
    },
    isLast: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pleadingText: '',
      accordionsOpened: {
        formAndPresentation: false,
        contentAndRelevance: false,
        teacherFeedback: false,
      },
    };
  },
  computed: {
    user(): UserModel {
      return this.$store.getters.currentUser;
    },
    evaluationSheet(): EvaluationSheet {
      return this.assessmentResult.evaluationSheet;
    },
    gradeGroups(): Array<GradeGroups> {
      const evalSheet = this.evaluationSheet;

      const formAndPresentationGrades = [
        { name: 'clarity', value: evalSheet.clarityGrade },
        { name: 'formatRules', value: evalSheet.formatRulesGrade },
        { name: 'structure', value: evalSheet.structureGrade },
        { name: 'discourse', value: evalSheet.discourseGrade },
      ];

      const contentAndRelevanceGrades = [
        { name: 'content', value: evalSheet.contentGrade },
        { name: 'innovation', value: evalSheet.innovationGrade },
        { name: 'originality', value: evalSheet.originalityGrade },
        { name: 'upToDateness', value: evalSheet.upToDatenessGrade },
        { name: 'contextualization', value: evalSheet.contextualizationGrade },
        { name: 'adherence', value: evalSheet.adherenceGrade },
      ];

      return [
        { name: 'formAndPresentation', grades: formAndPresentationGrades },
        { name: 'contentAndRelevance', grades: contentAndRelevanceGrades },
      ];
    },
    hasPassed(): boolean {
      return this.assessmentResult.status === ASSESSMENT_RESULT_STATUS.PASSED;
    },
    showPleaInput(): boolean {
      const { status } = this.assessmentResult;

      return (
        status === ASSESSMENT_RESULT_STATUS.FAILED_ELEGIBLE_FOR_REVALUATION ||
        status === ASSESSMENT_RESULT_STATUS.WAITING_REVALUATION
      );
    },
    disablePleaInput(): boolean {
      const { status } = this.assessmentResult;

      return status === ASSESSMENT_RESULT_STATUS.WAITING_REVALUATION;
    },
    titleMessage(): string {
      const result = this.hasPassed ? 'passed' : 'failed';
      const resultTitle = i18n.t(`yourAssessments.tcc.tabs.finalThesis.resultTitles.${result}`, {
        name: this.user.firstName,
      });

      return DOMPurify.sanitize(resultTitle.toString(), { USE_PROFILES: { html: true } });
    },
    resultImage(): string {
      const fileName = this.hasPassed ? 'yeah' : 'bananaSlip';
      return `${process.env.BASE_URL || ''}img/assessments/${fileName}.svg`;
    },
    plagiarismIconName(): string {
      return this.evaluationSheet.isPlagiarism ? 'close' : 'check';
    },
    plagiarismMessage(): LocaleMessage {
      const result = this.evaluationSheet.isPlagiarism ? 'has' : 'doesNotHave';
      return i18n.t(`yourAssessments.tcc.tabs.finalThesis.plagiarism.${result}`);
    },
    canSendPlea(): boolean {
      const { status } = this.assessmentResult;

      return (
        status === ASSESSMENT_RESULT_STATUS.FAILED_ELEGIBLE_FOR_REVALUATION &&
        this.pleadingText.trim().length > 0
      );
    },
  },
  beforeMount() {
    if (this.assessmentResult.status === ASSESSMENT_RESULT_STATUS.WAITING_REVALUATION)
      this.pleadingText = this.assessmentResult.lastReply.answer || '';
  },
  methods: {
    sendPlea(): void {
      if (!this.canSendPlea) return;

      this.$emit('sendPlea', this.pleadingText);
    },
    onAccordionOpens(elementName: string): void {
      this.accordionsOpened.formAndPresentation = elementName === 'formAndPresentation';
      this.accordionsOpened.contentAndRelevance = elementName === 'contentAndRelevance';
      this.accordionsOpened.teacherFeedback = elementName === 'teacherFeedback';
    },
  },
});
